import React, { FC, useContext, useState } from 'react';
import { Box, Tab, Tabs, useTheme } from '@mui/material';
import { Chat, History, MarkChatRead } from '@mui/icons-material';
import ReviewReply from './ReviewReply';
import Comment from '../Comment/Comment';
import { IReviewComment } from '../../shared/interfaces/IReviewComment';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import { IComment } from '../../shared/interfaces/IComment';
import ReviewReplyGMB from './ReviewReplyGMB';
import TabPanel from '../TabPanel/TabPanel';
import ReviewNew from './ReviewNew';
import Helper from '../../shared/helpers/Helper';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../providers/AuthProvider';

interface Props {
  reviewId: string;
  review: any;
  locationId?: string;
  gmbReviewId?: string;
  platform?: string;
  reviewComment?: IReviewComment;
  companyComment?: IComment | null;
  input: string | null | undefined;
  username?: string | null;
  communityManagement?: CommunityManagement;
  commentingModule: boolean;
  hide: boolean;
  history?: any[];
}

const ReviewFooter: FC<Props> = ({
  reviewId,
  review,
  locationId,
  gmbReviewId,
  reviewComment,
  input,
  communityManagement,
  commentingModule,
  username,
  platform,
  companyComment,
  hide,
  history,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [commentExpanded, setCommentExpanded] = useState<boolean>(false);
  const { hasRole } = useContext(AuthContext);

  const [currentTab, setCurrentTab] = useState<number>(-1);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  if (hide || !communityManagement) {
    return null;
  }

  const showHistory = hasRole('can-use-reviewhistory') && history && history.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          borderBottom: currentTab === -1 ? 0 : 1,
          borderColor: 'divider',
          ml: theme.spacing(2),
          mr: theme.spacing(2),
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
        >
          {showHistory && (
            <Tab
              aria-label="Historie anzeigen"
              value={3}
              icon={<History />}
            />
          )}
          {commentingModule && (
            <Tab
              aria-label="Kommentar anzeigen"
              value={0}
              icon={<Chat />}
            />
          )}
          {!commentingModule && companyComment && (
            <Tab
              aria-label="Kommentar anzeigen"
              value={1}
              icon={<MarkChatRead />}
            />
          )}
          {!companyComment && review.response && review.response.comment && (
            <Tab
              aria-label="Kommentar anzeigen"
              value={2}
              icon={<MarkChatRead />}
            />
          )}
        </Tabs>
      </Box>
      <Box
        sx={{
          ml: theme.spacing(1),
          mr: theme.spacing(1),
        }}
      >
        {commentingModule && (
          <TabPanel
            value={currentTab}
            index={0}
            onCloseClick={() => setCurrentTab(-1)}
          >
            {companyComment && (
              <Comment
                companyComment={companyComment}
                sx={{ mt: theme.spacing(2) }}
              />
            )}
            {platform === 'google' ? (
              <ReviewReplyGMB
                id={reviewId}
                reviewId={gmbReviewId || ''}
                locationId={locationId || ''}
                input={input || ''}
                username={username}
                reviewComment={reviewComment}
                communityManagement={communityManagement}
              />
            ) : (
              <ReviewReply
                reviewId={reviewId}
                username={username}
                reviewComment={reviewComment}
                communityManagement={communityManagement}
                input={input || ''}
              />
            )}
          </TabPanel>
        )}
        {!commentingModule && companyComment && (
          <TabPanel
            value={currentTab}
            index={1}
            onCloseClick={() => setCurrentTab(-1)}
          >
            <Comment
              companyComment={companyComment}
              sx={{ mt: theme.spacing(2) }}
            />
          </TabPanel>
        )}
        {!companyComment && review.response && review.response.comment && (
          <TabPanel
            value={currentTab}
            index={2}
            onCloseClick={() => setCurrentTab(-1)}
          >
            <Comment
              companyComment={{
                comment: review.response.comment,
                date: review.timestamp || review.date,
                name: review.name,
                position: '',
                timestamp: '',
                username: '',
                userName: '',
              }}
            />
          </TabPanel>
        )}
        {showHistory && (
          <TabPanel
            value={currentTab}
            index={3}
            onCloseClick={() => setCurrentTab(-1)}
          >
            {history.map((item: any, i: number) => {
              return (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'left', gap: '1em' }}>
                    <div>
                      <span>{t('created_on')} </span>
                      <strong>{Helper.getFormattedDate(item.timestamp, true)}</strong>
                    </div>
                    {item.scrape_time && (
                      <div>
                        <span>{t('found_on')} </span>
                        <strong>{Helper.getFormattedDate(item.scrape_time, true)}</strong>
                      </div>
                    )}
                  </Box>
                  <ReviewNew
                    key={i}
                    reviews={[
                      {
                        id: reviewId,
                        platform: platform || '',
                        identifier: '',
                        company: '',
                        createdAt: '',
                        updatedAt: '',
                        firstScrapedAt: '',
                        reviewerRole: '',
                        lastScrapedAt: '',
                        review: item,
                        timestamp: item.timestamp,
                        scrapeCount: 0,
                        note: '',
                        deleted: false,
                        deletion: false,
                      },
                    ]}
                    monitoring={[]}
                    commentingModule={false}
                    hideHeader={true}
                    hideFooter={true}
                    location={[]}
                  />
                </>
              );
            })}
          </TabPanel>
        )}
      </Box>
    </Box>
  );
};

export default ReviewFooter;
